'use client';
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { toast } from 'react-hot-toast';

import { Portal } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';
import { useWatchlistContext } from '@benzinga/user-context';
import { AutocompleteManager, AutocompleteSymbol } from '@benzinga/autocomplete-manager';
import { Watchlist, WatchlistManager } from '@benzinga/watchlist-manager';

type DropdownPosition = {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
};

export const TickerSearch = ({
  placeholder,
  onTickerClick,
  hideType = false,
}: {
  placeholder: string;
  onTickerClick?: (ticker: string) => void;
  hideType?: boolean;
}) => {
  const { selectedWatchlist, setAddTickerModal } = useWatchlistContext();
  const [tickerSearch, setTickerSearch] = useState('');
  const [searchResult, setSearchResult] = useState<AutocompleteSymbol[]>([]);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const session = useContext(SessionContext);
  const autocompleteManager = session.getManager(AutocompleteManager);
  const watchlistManager = session.getManager(WatchlistManager);
  const showResults = searchResult.length > 0;

  const updateDropdownPosition = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;

    const rect = root.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const estimatedHeight = Math.min(320, viewportHeight * 0.45);
    const openUpward = spaceBelow < estimatedHeight && rect.top > spaceBelow;

    setDropdownPosition({
      left: rect.left,
      top: openUpward ? Math.max(8, rect.top - estimatedHeight - 4) : rect.bottom + 4,
      width: rect.width,
      maxHeight: openUpward ? Math.min(estimatedHeight, rect.top - 12) : Math.min(estimatedHeight, spaceBelow - 12),
    });
  }, []);

  const searchTicker = useCallback(
    async (query: string) => {
      setTickerSearch(query);
      if (query === '') {
        setSearchResult([]);
        return;
      }
      const tickersResponse = await autocompleteManager.getAutocompleteListing(query);
      if (tickersResponse.ok) {
        setSearchResult(tickersResponse.ok.result);
      }
    },
    [autocompleteManager],
  );

  const handleTickerClick = async (ticker: string) => {
    if (onTickerClick) {
      onTickerClick(ticker);
    } else if (selectedWatchlist?.watchlistId === 'overview') {
      setAddTickerModal({ show: true, ticker, isLoading: false });
    } else {
      const result = await watchlistManager.addTickersToWatchlist(selectedWatchlist as Watchlist, [ticker]);
      if (result.ok) {
        toast.success(`${ticker} added to ${selectedWatchlist?.name ?? 'watchlist'}`);
      } else {
        toast.error('Error adding ticker to watchlist. Please try again.');
      }
    }
    setTickerSearch('');
    setSearchResult([]);
  };

  useLayoutEffect(() => {
    if (!showResults) {
      setDropdownPosition(null);
      return;
    }
    updateDropdownPosition();
  }, [showResults, searchResult.length, updateDropdownPosition]);

  useEffect(() => {
    if (!showResults) return;

    const handleReposition = () => updateDropdownPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [showResults, updateDropdownPosition]);

  useEffect(() => {
    if (!showResults) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setSearchResult([]);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [showResults]);

  return (
    <div ref={rootRef} className="relative flex min-h-10 w-full flex-grow flex-row items-center gap-2 bg-transparent px-3 py-2">
      <FaSearch size={12} className="shrink-0 text-slate-400" />
      <input
        className="h-full min-w-0 flex-grow bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
        type="text"
        placeholder={placeholder}
        onChange={event => searchTicker(event.target.value)}
        value={tickerSearch}
        onFocus={() => searchTicker(tickerSearch)}
        autoComplete="off"
      />
      {showResults && dropdownPosition && (
        <Portal>
          <div
            ref={dropdownRef}
            className="fixed z-[300] overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
            style={{
              left: dropdownPosition.left,
              top: dropdownPosition.top,
              width: dropdownPosition.width,
              maxHeight: dropdownPosition.maxHeight,
            }}
          >
            <table className="w-full">
              <tbody>
                {searchResult.map(result => (
                  <tr
                    key={result.symbol}
                    className="cursor-pointer border-b border-slate-100 last:border-b-0 hover:bg-bzblue-100"
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => handleTickerClick(result.symbol)}
                  >
                    {!hideType && (
                      <td className="w-8 px-3 py-2.5 text-xs font-semibold uppercase text-slate-400">{result.type}</td>
                    )}
                    <td className="px-3 py-2.5 text-sm text-slate-800">
                      <strong className="font-bold text-slate-950">{result.symbol}</strong>
                      <span className="mx-2 text-slate-300">|</span>
                      <span className="text-slate-600">{result.shortName}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Portal>
      )}
      {tickerSearch.length > 0 && (
        <button
          type="button"
          aria-label="Clear search"
          className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          onClick={() => {
            setTickerSearch('');
            setSearchResult([]);
          }}
        >
          <MdClose size={16} />
        </button>
      )}
    </div>
  );
};
